<script>
  // Global search palette (⌘K / Ctrl+K). Two result kinds share one keyboard-
  // navigable list: admin pages ("jump to", matched client-side from the sidebar
  // nav the layout passes in) and records (grouped matches from the shared `global_search`
  // prop, AdminSite::GlobalSearchProp).
  // ↑/↓ move the highlight across groups, Enter (or click) navigates, Esc closes.
  import { router } from '@inertiajs/svelte'
  import Badge from './ui/badge.svelte'
  import Icon from './ui/icon.svelte'
  import { isSearchHotkey } from '../lib/search.js'
  import { reloadSharedProp } from '../lib/shared_props.js'

  // `scope` is the selected College Town's name (null = all towns) — record
  // results are already filtered to it server-side via the session, the chip
  // just makes that visible.
  let { open = $bindable(false), pages = [], scope = null } = $props()

  let q = $state('')
  let recordGroups = $state([])
  let highlighted = $state(0)
  let loading = $state(false)
  let inputEl = $state(null)
  let listEl = $state(null)

  const term = $derived(q.trim())

  // With no query the palette is a "jump to page" launcher; with one, pages
  // filter down alongside the server's record matches.
  const pageMatches = $derived(
    pages
      .filter((page) => !term || page.label.toLowerCase().includes(term.toLowerCase()))
      .map((page) => ({ value: page.href, label: page.label, sublabel: page.section, icon: page.icon, url: page.href }))
  )

  const groups = $derived.by(() => {
    const out = []
    if (pageMatches.length) out.push({ type: 'pages', label: 'Pages', items: pageMatches })
    for (const group of recordGroups) {
      out.push({ ...group, items: group.items.map((item) => ({ ...item, icon: group.icon })) })
    }
    return out
  })

  const flat = $derived(groups.flatMap((group) => group.items))

  // Flat index of each group's first item, so grouped markup can highlight by
  // one list-wide index.
  const groupOffsets = $derived.by(() => {
    const offsets = []
    let offset = 0
    for (const group of groups) {
      offsets.push(offset)
      offset += group.items.length
    }
    return offsets
  })

  $effect(() => {
    if (!open) return
    q = ''
    recordGroups = []
    highlighted = 0
    inputEl?.focus()
  })

  // Debounced server search; a stale answer (an earlier term) is dropped rather than raced.
  $effect(() => {
    const current = term
    highlighted = 0
    if (!open || !current) {
      recordGroups = []
      loading = false
      return
    }

    loading = true
    let stale = false
    const timer = setTimeout(async () => {
      try {
        const answer = await reloadSharedProp('global_search', { global_search_q: current })
        if (stale || answer?.q !== current) return
        recordGroups = answer.results
      } catch {
        if (stale) return
        recordGroups = []
      }
      loading = false
    }, 200)

    return () => {
      stale = true
      clearTimeout(timer)
    }
  })

  $effect(() => {
    listEl?.querySelector(`[data-search-index="${highlighted}"]`)?.scrollIntoView({ block: 'nearest' })
  })

  const close = () => {
    open = false
  }

  const visit = (item) => {
    close()
    router.visit(item.url)
  }

  const onwindowkeydown = (event) => {
    if (isSearchHotkey(event)) {
      event.preventDefault()
      open = !open
    } else if (open && event.key === 'Escape') {
      close()
    }
  }

  const onkeydown = (event) => {
    if (!flat.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      highlighted = (highlighted + 1) % flat.length
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      highlighted = (highlighted - 1 + flat.length) % flat.length
    } else if (event.key === 'Enter') {
      event.preventDefault()
      visit(flat[highlighted])
    }
  }
</script>

<svelte:window onkeydown={onwindowkeydown} />

<dialog class="modal items-start pt-[12vh]" class:modal-open={open} aria-label="Global search">
  <div class="modal-box w-full max-w-xl p-0 overflow-hidden">
    <div class="flex items-center gap-3 px-4 border-b border-base-300">
      <Icon name="search" size="sm" class="shrink-0 text-base-content/60" />
      {#if scope}
        <Badge variant="primary" soft size="sm" icon="college_towns" class="shrink-0 max-w-40" title={`Results scoped to ${scope}`}>
          <span class="truncate">{scope}</span>
        </Badge>
      {/if}
      <input
        bind:this={inputEl}
        bind:value={q}
        {onkeydown}
        type="text"
        placeholder="Search pages, places, quests, users…"
        class="grow py-3 bg-transparent outline-none"
        role="combobox"
        aria-expanded={flat.length > 0}
        aria-controls="global-search-results"
        aria-label="Search everything"
      />
      {#if loading}
        <span class="loading loading-spinner loading-xs text-base-content/60" aria-label="Searching"></span>
      {/if}
    </div>

    <div id="global-search-results" bind:this={listEl} class="max-h-96 overflow-y-auto p-2" role="listbox">
      {#each groups as group, groupIndex (group.type)}
        <div class="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-base-content/60">{group.label}</div>
        {#each group.items as item, itemIndex (item.value)}
          {@const index = groupOffsets[groupIndex] + itemIndex}
          <button
            type="button"
            data-search-index={index}
            role="option"
            aria-selected={index === highlighted}
            class={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left ${index === highlighted ? 'bg-primary/10 text-primary' : ''}`}
            onclick={() => visit(item)}
            onmouseenter={() => (highlighted = index)}
          >
            <Icon name={item.icon} size="sm" class="shrink-0" />
            <span class="truncate font-medium">{item.label}</span>
            {#if item.sublabel}
              <span class={`truncate text-sm ${index === highlighted ? 'text-primary/70' : 'text-base-content/60'}`}>{item.sublabel}</span>
            {/if}
          </button>
        {/each}
      {/each}

      {#if !flat.length && term && !loading}
        <div class="px-3 py-8 text-center text-sm text-base-content/60">
          No matches for “{term}”{scope ? ` in ${scope}` : ''}
        </div>
      {/if}
    </div>

    <div class="flex items-center gap-4 border-t border-base-300 px-4 py-2 text-xs text-base-content/60">
      <span class="flex items-center gap-1"><kbd class="kbd kbd-xs">↑</kbd><kbd class="kbd kbd-xs">↓</kbd> navigate</span>
      <span class="flex items-center gap-1"><kbd class="kbd kbd-xs">↵</kbd> open</span>
      <span class="flex items-center gap-1"><kbd class="kbd kbd-xs">esc</kbd> close</span>
    </div>
  </div>

  <button type="button" class="modal-backdrop" aria-label="Close search" onclick={close}></button>
</dialog>
